<template>
  <!-- eslint-disable max-len -->
  <div v-show="isContentHydrated">
    <!-- start/stop date updated due to email domain check -->
    <Banner
      v-if="(coa.medUpdated && coa.startDate) || (sdUpdated && coa.stopDate)"
      class="mt-6"
    >
      <template #heading>
        <!-- Start Update Updated Banner -->
        <p
          v-if="coa.medUpdated"
          ref="startUpdatedBanner"
        >
          Your mail forwarding start date has been updated to {{ parseAndFormatDate(coa.startDate) }}
        </p>
        <!-- Stop Update Updated Banner -->
        <p
          v-if="sdUpdated"
          ref="stopUpdatedBanner"
          class="mt-2"
        >
          Your mail forwarding end date has been updated to {{ parseAndFormatDate(coa.stopDate) }}
        </p>
      </template>
    </Banner>
    <div class="c-hairline mt-6" />
    <section class="py-4">
      <h3 class="font-bold text-xl">
        Move Information
      </h3>
      <!-- Display if the modalNewConstruction is opened and they hit "Yes" or "No" -->
      <template
        v-if="overrideAms"
      >
        <ErrorAlert
          v-if="$route.query.errorException === 'NewAddressFieldFullAddressException'"
          ref="newAddressFieldFullAddress"
          class="mt-2"
        >
          We cannot find the address in our records. The "Street Address" field should not include the city, state or ZIP. Please click the "Edit" button to remove the city, state or ZIP, then click "Save." If you are sure this is the correct address, you may proceed by clicking "Next."
        </ErrorAlert>
        <ErrorAlert
          v-if="$route.query.errorException === 'InvalidPrimaryInformationException'"
          ref="invalidPrimaryInformation"
          class="mt-2"
        >
          We cannot locate the entered street number in our records. Please click the "Edit" button to correct the street number of the street address, then click "Save." If you are certain the existing address is correct, you may proceed by clicking "Next."
        </ErrorAlert>
        <ErrorAlert
          v-if="$route.query.errorException === 'UnneededSecondaryInformationException'"
          ref="unneededSecondaryInformation"
          class="mt-2"
        >
          According to our records, there is no unit or apartment number associated with this address. Please verify the address for accuracy and click "Edit" then "Save" to make corrections.
        </ErrorAlert>
        <ErrorAlert
          v-if="$route.query.errorException === 'InvalidSecondaryInformationException'"
          ref="invalidSecondaryInformation"
          class="mt-2"
        >
          A unit or apartment number is either missing, incomplete or invalid. Please click "Edit" to add the correct number/designation after the street name in the "Street" field, then click "Save." USPS will standardize your address upon submission.
        </ErrorAlert>
        <ErrorAlert
          v-if="$route.query.errorException === 'AmsInvalidAddressException'"
          ref="invalid"
          class="mt-2"
        >
          We cannot find the address you're moving to in our records. Please verify the address for accuracy and click "Edit" then "Save" to make corrections.
        </ErrorAlert>
        <ErrorAlert
          v-if="isMultipleAddressCoa"
          ref="invalid"
          class="mt-2"
        >
          We cannot find the address you're moving to in our records. Please verify the address for accuracy and click "Edit" then "Update" to make corrections. If you are certain the existing address is correct, you may proceed by clicking "Next."
        </ErrorAlert>
        <ErrorAlert
          ref="overrideMessage"
          class="mt-2"
        >
          Errors with your address may result in delays with mail forwarding.
        </ErrorAlert>
      </template>
      <div class="mt-4">
        <CoaItem
          v-if="coa.moverType === 'BUSINESS'"
          id="business-name"
          label="Business Name"
          class="alias-list"
        >
          {{ coa.businessName }}
          <span
            v-for="alias in businessAliases"
            :key="alias"
          >
            <br>{{ alias }}
          </span>
        </CoaItem>
        <CoaItem
          v-else
          label="Name"
        >
          {{ [mover.firstName, mover.middleName, mover.lastName] | formatName }}
        </CoaItem>
        <CoaItem
          label="Who's Moving?"
          class="mt-4"
        >
          {{ coa.moverType }}
        </CoaItem>
        <CoaItem
          label="Email Address"
          class="mt-4"
        >
          {{ mover.email }}
        </CoaItem>
        <CoaItem
          label="Phone Number"
          class="mt-4"
        >
          {{ (mover.homePhone || mover.mobilePhone) | formatPhone }}
        </CoaItem>
        <CoaItem
          label="Phone Type"
          class="mt-4"
        >
          {{ mover.homePhone && 'Home' }}
          {{ mover.mobilePhone && 'Mobile' }}
        </CoaItem>
        <CoaItem
          label="Type of Move"
          class="mt-4"
        >
          {{ coa.forwardType }}
        </CoaItem>
        <CoaItem
          v-if="coa.forwardType === 'PERMANENT'"
          label="Requested Mail Forwarding Date"
          class="mt-4"
        >
          <!-- Monday, September 14, 2020 -->
          {{ parseAndFormatDate(coa.startDate) }}
        </CoaItem>
        <template v-if="coa.forwardType === 'TEMPORARY'">
          <CoaItem
            label="Requested Start Forwarding Date"
            class="mt-4"
          >
            {{ parseAndFormatDate(coa.startDate) }}
          </CoaItem>
          <CoaItem
            label="Requested End Forwarding Date"
            class="mt-4"
          >
            {{ parseAndFormatDate(coa.stopDate) }}
          </CoaItem>
        </template>
        <CoaItem
          label="Old Address"
          class="mt-4"
        >
          {{ oldAddress.amsCompleteAddress }}<br>
          {{ oldAddress.amsCity }}, {{ oldAddress.amsState }} {{ formatZip9(oldAddress.amsZip9) }}<br>
          {{ oldAddress.amsUrbanizationName }}
        </CoaItem>
        <CoaItem
          label="New Address"
          class="mt-4"
        >
          {{ newAddress.amsCompleteAddress }}<br>
          {{ newAddress.amsCity }}, {{ newAddress.amsState }} {{ formatZip9(newAddress.amsZip9) }}<br>
          {{ newAddress.amsUrbanizationName }}
        </CoaItem>
      </div>
      <router-link
        id="verify-info-edit"
        :to="{ path: `edit${editPagePathParam()}` }"
        class="c-btn c-btn--secondary mt-6"
        @click.native="analytics.click.edit"
      >
        Edit
      </router-link>
    </section>
    <div v-if="enhancedBusinessFlowEnabled && coa.moverType === 'BUSINESS' && hasMoreThanOneAddress">
      <div class="c-hairline mt-6" />
      <AddressList
        ref="verify-info-addresses"
        :is-verify-info="true"
        :is-simplify="!!showSimplifyExperience"
        :business="business"
        :bus-address="busAddresses.addresses||[]"
        :selected="orderId"
        :mobile="true"
        @address-clicked="(orderId)=>populateBusiness(orderId)"
      />
    </div>
    <div class="c-hairline mt-6" />
    <form
      ref="form"
      @submit.prevent="submit"
    >
      <template v-if="showChoiceExperience">
        <p class="choice__experience--cart--header">
          Order Summary
        </p>
        <p class="choice__experience--cart--paragraph">
          This payment will be processed with the {{ asCurrency(choiceSelections[0].cost) }}
          identity verification fee.
        </p>
        <p
          v-if="extensionSelected > 0"
          class="choice__experience--cart--paragraph"
        >
          This is a non-refundable product. Cancelling your
          Change-of-Address will not refund you Extended
          Mail Service Fee.
        </p>
        <p
          v-if="coa.moverType === 'BUSINESS' && extensionSelected > 0"
          ref="emfIcoaAlias"
          class="choice__experience--cart--paragraph"
        >
          Note: The Extended Mail Forwarding only applies to the primary business name.
          If you want Extended Mail Forwarding for Business Alias names, you must access
          the Official USPS Change-of-Address Correct Form at
          <a
            class="c-link--inline weight-normal"
            rel="noopener noreferrer"
            :href="coacUrl"
            target="new"
          >{{ coacUrl }}</a>
        </p>
        <div class="choice__experience--services">
          <table class="choice__experience--shopping-cart">
            <thead>
              <tr>
                <th>Item</th>
                <th />
                <th>Price</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="choice in choiceSelections"
                :key="choice.id || choice.optionId"
              >
                <td> {{ choice.title }} </td>
                <td
                  class="choice__selection--row"
                  :class="{'choice__selection--row--selectable': !isRequired(choice)}"
                >
                  <img
                    v-if="!isRequired(choice)"
                    :id="`order-summary-close-icon-${choice.id}`"
                    loading="lazy"
                    style="margin: auto;"
                    class="choice__selection--icon"
                    :src="require('../../assets/choice/icon_choice_close.svg')"
                    alt=""
                    data-alternative-type="checkbox"
                    :data-alternative-value="choice.id"
                    :data-text="choice.id > 0 ? 'Mail Forwarding Extension' : 'Informed Delivery'"
                    data-event-name="ElementClicked"
                    data-outcome="TOGGLE_OFF"
                    data-page-name="VERIFY_INFO"
                    @click="commitSelection($event)"
                  >
                  <small
                    v-if="!isRequired(choice)"
                    :id="`order-summary-remove-${choice.id}`"
                    style="padding: 0 2px;"
                    :class="{'choice__selection--remove': !isRequired(choice)}"
                    data-alternative-type="checkbox"
                    :data-alternative-value="choice.id"
                    :data-text="choice.id > 0 ? 'Mail Forwarding Extension' : 'Informed Delivery'"
                    data-event-name="ElementClicked"
                    data-outcome="TOGGLE_OFF"
                    data-page-name="VERIFY_INFO"
                    @click="commitSelection($event)"
                  >
                    Remove
                  </small>
                </td>
                <td> {{ asCurrency(parseCost(choice)) }} </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td>Total due on next step</td>
                <td />
                <td> {{ asCurrency(choiceSelectionsTotal) }} </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div
          class="choice__experience--services--grid-item"
          :class="{
            'verify__info--autofill': noSelections,
            'additional__service--selections': !noSelections,
          }"
        >
          <section
            :class="{
              'additional-services-selections-hidden': !showSelections
            }"
            class="additional-services-selections"
          >
            <template>
              <h3 class="additional-services-selections-title">
                Your additional {{ pluralize('selection', additionalSelections) }}
              </h3>
              <header class="additional-services-selections-header">
                Item
              </header>
              <article
                v-if="isSelected('vr')"
                key="vr"
                class="additional-services-selections-item"
              >
                <span
                  class="additional-services-selections-item-name"
                  :class="{ 'ineligible-voter-reg': !isPermanent }"
                >
                  Voter Registration
                </span>
                <span>&nbsp;</span>
                <button
                  v-if="isPermanent"
                  data-choiceselection="vr"
                  class="additional-services-selections-remove"
                  data-text="Voter Registration"
                  data-outcome="TOGGLE_OFF"
                  data-event-name="ButtonClicked"
                  data-page-name="VERIFY_INFO"
                  type="button"
                  @click="removeSelection($event,'vr')"
                >
                  <img
                    class="additional-services-selections-choice-icon"
                    loading="lazy"
                    alt="blue circle with a while letter X."
                    :src="require('../../assets/choice/icon_choice_close.svg')"
                  >
                  <small v-if="isPermanent" />
                  <img
                    v-if="isPermanent"
                    class="additional-services-selections-choice-icon"
                    :src="require('../../assets/choice/icon_circle_checkmark.svg')"
                    alt="blue checkmark set inside a thin blue circle."
                    loading="lazy"
                  >
                </button>
              </article>
              <article
                v-if="isSelected('save')"
                class="additional-services-selections-item"
              >
                <span class="additional-services-selections-item-name"> Exclusive Offer from MYMOVE </span>
                <span>&nbsp;</span>
                <button
                  data-choiceselection="save"
                  class="additional-services-selections-remove"
                  data-text="Exclusive Offers"
                  data-event-name="ButtonClicked"
                  data-outcome="TOGGLE_OFF"
                  data-page-name="VERIFY_INFO"
                  type="button"
                  @click="removeSelection($event,'save')"
                >
                  <img
                    class="additional-services-selections-choice-icon"
                    loading="lazy"
                    alt="blue circle with a while letter X."
                    :src="require('../../assets/choice/icon_choice_close.svg')"
                  >
                  <small />
                  <img
                    class="additional-services-selections-choice-icon"
                    :src="require('../../assets/choice/icon_circle_checkmark.svg')"
                    alt="blue checkmark set inside a thin blue circle."
                    loading="lazy"
                  >
                </button>
              </article>
            </template>
            <info-panel
              id="temporary-mover-no-vr"
              :display-panel="!isPermanent"
              has-note
              note-bold
              info-type="info"
              :info-message="staticText.tempMoverNoVR"
            />
          </section>
          <div
            v-if="showSelections"
            class="c-hairline"
          />
          <p
            v-if="showSelections"
            class="additional__service--registration"
            :class="{
              'choice--selection-verify-info-autofill': showSelections
            }"
          >
            Want to save time? Pre-fill all your info.
          </p>
          <p
            v-if="!showSelections"
            id="choice--selection-verify-info-autofill"
          >
            Join MYMOVE to access brand-name deals, moving
            tools, tips and start your voter registration
          </p>
          <div
            v-if="showSelections"
            class="choice__selection--input"
            :class="{'choice__selection--input--selected': myMoveChoicePrefillOptIn}"
          >
            <label class="choice__selection--label">
              Yes, pre-fill my forms
              <input
                ref="verify-info-autofill-vr"
                v-model="myMoveChoicePrefillOptIn"
                data-mercurial-name="myMoveChoicePrefillOptIn"
                name="myMoveChoicePrefillOptIn"
                data-text="Prefill Opt In"
                data-event-name="ElementClicked"
                data-outcome="PREFILL_OPT_IN"
                data-page-name="VERIFY_INFO"
                class="mt-3"
                type="checkbox"
                @input="handleChoice($event)"
              >
              <span class="checkmark" />
            </label>
          </div>
          <p
            class="additional__service--consent"
            :class="{
              'choice__services--consent': !showSelections
            }"
          >
            <label
              class="choice__selection--label"
              :class="{'choice__services--consent--inactive': showSelections}"
            >
              <input
                ref="informed-delivery-opt-in"
                v-model="myMovePrefillOptIn"
                name="myMovePrefillOptIn"
                data-mercurial-name="myMovePrefillOptIn"
                data-text="Prefill Opt In Choice Selections Removed"
                data-event-name="ElementClicked"
                data-outcome="PREFILL_OPT_IN"
                data-page-name="VERIFY_INFO"
                class="mt-3"
                type="checkbox"
                @input="handleChoice($event)"
              >
              <span class="checkmark" />
            </label>
            {{ staticText.myMoveConsent }}
          </p>
        </div>
      </template>
      <!-- Extended Mail Forwarding - EMF -->
      <template v-if="!showChoiceExperience && flagEMFEnabled">
        <transition
          appear
          name="fade"
        >
          <div
            v-if="extensions.length"
            class="mt-6"
          >
            <Tooltip>
              <h3
                id="emf-non-refund-heading"
                class="font-bold text-xl flex flex-row align-top"
              >
                Extend your Mail Forwarding End Date
                <span class="text-sm ml-1 mr-3 pt-1">
                  (Optional)
                </span>
              </h3>
              <p
                id="emf-non-refund-subheading"
                class="font-bold my-3"
              >
                Note: Nonrefundable additional charge applies.
              </p>
              <p
                v-if="emf && coa.moverType === 'BUSINESS'"
                id="emf-primary-subheading"
                ref="emfIcoaAlias"
                class="font-bold my-3"
              >
                Note: The Extended Mail Forwarding only applies to the primary business name.
                If you want Extended Mail Forwarding for Business Alias names, you must access
                the Official USPS Change-of-Address Correct Form at
                <a
                  class="c-link--inline weight-normal"
                  rel="noopener noreferrer"
                  :href="coacUrl"
                  target="new"
                >{{ coacUrl }}</a>
              </p>
              <template #tip>
                <div class="text-sm">
                  <p>
                    After completing a Change-of-Address, your mail will be forwarded to your new address for 12 months following the Mail Forwarding Start Date you provided.
                  </p>
                  <p class="mt-3">
                    For your added convenience, we offer you the option of extending your Mail Forwarding End Date now for an additional cost.
                  </p>
                </div>
              </template>
              <template slot="accessible">
                Extended mail forwarding details
              </template>
            </Tooltip>
            <InputDropdown
              v-model="emf"
              label="Extension Period"
              name="extended-mail-forwarding"
              @input="$event && analytics.input.emf($event)"
            >
              <option value="">
                Select
              </option>
              <option
                v-for="extension in extensions"
                :key="extension.optionLFED"
                :value="extension.optionId"
              >
                {{ extension.optionPeriod }} months - ${{ extension.optionCost }}
              </option>
            </InputDropdown>
            <ExtensionSummary
              v-if="emf || informedDelivery"
              :extension-cost="emfProperties.optionCost"
              :extension-period="emfProperties.optionPeriod"
              :icoa-amount="choiceSelections[0].cost * 100 || 105"
              :informed-delivery="informedDelivery"
              show
              class="mt-6"
              @clear-selection="emf = '', analytics.click.removeEMF()"
              @clear-selection-informed="removeInformedDelivery(), analytics.click.removeInformedDelivery()"
            />
          </div>
        </transition>
      </template>
      <div
        v-if="!showChoiceExperience"
        class="mt-6 text-xs"
      >
        <p>
          <span class="font-bold">
            Why your name and address might look different than how you entered them:
          </span>
          Special characters in your name (like apostrophes) may have been removed and your address(es) may have been standardized to match the official U.S. Postal Service format. This helps forward your mail more effectively.
        </p>
      </div>
      <div
        id="experience"
        ref="experience"
      />
      <p
        v-if="emf"
        class="mt-6 text-sm"
      >
        By clicking "Next," I have read, understand and agree to the
        <a
          href="https://www.usps.com/terms-conditions/extended-mail-forwarding.htm"
          target="_blank"
          class="c-link--inline"
          rel="noopener noreferrer"
        >
          Terms and Conditions.
        </a>
        Extended Mail Forwarding is a nonrefundable product. You cannot change or cancel your Extended Mail Forwarding request.
      </p>
      <StickyPanel class="mt-6">
        <template slot="buttons">
          <router-link
            :to="{ name: handleBack, query: $route.query }"
            class="c-btn--mobile c-btn--secondary--mobile simplify__nav-button"
            replace
            @click.native="analytics.click.back()"
          >
            Back
          </router-link>
          <button
            id="next"
            ref="nextBtn"
            class="c-btn--mobile simplify__nav-button"
            @click="analytics.click.next()"
          >
            Next
          </button>
        </template>
      </StickyPanel>
    </form>
    <Modal
      ref="coaCheckModal"
      v-model="coaCheckModal"
    >
      <h4 class="font-bold">
        Based on your response...
      </h4>
      <p class="mt-3">
        A Change-of-Address already exists matching the name and address
        you've entered. To make changes to your current Change-of-Address,
        visit
        <a
          :class="['c-link--choice', 'c-link--inline']"
          :href="$options.coacUrl"
          rel="noopener noreferrer"
        >
          {{ $options.coacUrl }}
        </a>, enter your new ZIP Code™ and 16-digit confirmation code,
        which can be located within your confirmation email and the
        Confirmation Notification Letter you received at your new address.
      </p>
    </Modal>
  </div>
</template>

<script>
import {
  Banner,
  CoaItem,
  ErrorAlert,
  InputDropdown,
  Tooltip,
  Modal,
} from '@mgo/ui/lib/components';
import InfoPanel from '../../components/info-panel.vue';
import { mobileAnalytics } from './analytics';
import ExtensionSummary from '../../components/ExtensionSummary.vue';
import verifyInfoMixin from './mixin';
import StickyPanel from '../../components/StickyPanel.vue';
import AddressList from '../../components/MultipleAddresses/AddressList.vue';

export default {
  name: 'MobileVerifyInfoRead',
  components: {
    Banner,
    CoaItem,
    ErrorAlert,
    ExtensionSummary,
    InputDropdown,
    StickyPanel,
    Tooltip,
    InfoPanel,
    AddressList,
    Modal,
  },
  mixins: [verifyInfoMixin],
  data() {
    return {
      showExtendedMailForwarding: false,
      extensions: [],
      emf: '',
      aliases: [],
    };
  },
  computed: {
    totalSteps() {
      return !this.showChoiceExperience ? '7' : '8';
    },
    analytics() {
      return mobileAnalytics();
    },
    emfProperties() {
      if (!this.emf) {
        return {};
      }

      return this.extensions.find(({ optionId }) => optionId.toString() === this.emf);
    },
    icoaAmount() {
      return this.$store.get('icoaAmount')
        // Add in Biz Alias amounts
        + (this.$store.get('icoaAmount') * this.aliases?.length);
    },
    handleBack() {
      sessionStorage.setItem('myMovePrefillOptIn', this.myMovePrefillOptIn);
      sessionStorage.setItem('myMoveChoicePrefillOptIn', this.myMoveChoicePrefillOptIn);
      sessionStorage.setItem('informedDeliveryEligible', this.informedDeliveryEligible);
      sessionStorage.setItem('informedDelivery', this.informedDelivery);
      if (((this.isMultipleAddressCoa && this.isMultiBuzPrimaryPermanent)
      || (!this.isMultipleAddressCoa && this.isPermanent))
     && (this.showChoiceExperience || this.showNonControlExperience)) {
        return 'additional-services';
      }
      if (this.showChoiceExperience || this.showNonControlExperience) {
        return 'contact-info';
      }
      return 'move-combined';
    },
  },
  async mounted() {
    // enable loader for redirect
    this.$store.commit('ENABLE_LOADER');

    // await the first request
    // so we don't request multiple auth tokens
    await this.hydrateCOA();

    // grab the icoa dataset
    const { uuid, ...coa } = await this.$coa.coaGet() || {};

    // grab the type
    this.moverType = coa.moverType;

    if (this.moverType === 'BUSINESS') {
      const { aliases } = await this.$coa.businessAliasGetAll();
      this.aliases = aliases || [];
    }

    /*
     * redirect back to disclaimer
     * if base data (startDate) isn't available
     * or coa complete is true
     */
    if (!this.coa.startDate || this.coa.complete) {
      this.$router.push({ name: 'disclaimer' });
      // disable loader and return to avoid throwing getExtensions errors
      this.$store.commit('DISABLE_LOADER');
      return;
    }

    await Promise.all([
      this.hydrateMover(),
      this.hydrateAddresses(),
    ]);

    if (this.flagAmpersendEnabled) {
      await this.$amp.getUnimpressedExperiences();
    }

    // Init selected emf extension
    if (!this.showChoiceExperience && this.flagEMFEnabled) {
      this.emf = `${(this.extensionSelected || '')}`;
    }

    this.$store.commit('DISABLE_LOADER');
  },
  methods: {
    async submit() {
      this.$store.commit('ENABLE_LOADER');

      if (this.flagEMFEnabled) {
        if (!this.showChoiceExperience) {
          this.extensionSelected = this.emf ? parseInt(this.emf, 10) : 0;
        }
        await this.$coa.extensionSave(this.emf || 0, this.coa?.visitId);
      }

      this.next();
    },
    removeInformedDelivery() {
      this.informedDelivery = false;
      sessionStorage.removeItem('informedDelivery');
    },
  },
};
</script>
<style>
@import '../../css/components/button-card.css';
@import '../../css/components/additional-services-cart.css';
@import '../../css/components/choice-verify-info.css';
</style>
